import React, {useState} from 'react';

import {ChatRoomsEnum, FeaturesEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useBookingSummary} from 'presentation/modules/booking/shared/hooks/useBookingSummary';
import {useCallChatRoom, useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {FlowEnum} from '../../constants';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useOpenDrawerFilterStore} from 'presentation/modules/booking/shared/store/useOpenDrawerFilterStore';
import {BookingNotFoundStateVariantEnum} from '../../components/BookingNotFoundState/types';
import {ChangeFlowVariantEnum} from '../../components/ChangeFlowDialog/constants';

import {
	MAX_DISTANCE_FILTER_UNITS,
	MAX_DISTANCE_FILTER_UNITS_ODONTO,
} from '../useBookingUnits/constants';
import {UseBookingNotFoundProps} from './types';
import {UNITS_CHANNELS_CONTACTS} from 'presentation/modules/booking/shared/constants/unitsChannelsContacts';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

const {consultNetworkProviderHealth, consultNetworkProviderOdonto} =
	translations['pt-br'].privateFlow.profile.locationSelection;
const {unitsFlow, professionalFlow, districtFlow} = translations['pt-br'].privateFlow.bookingFlow;
const {noResults, callToChat, emptyDescription, emptyDescriptionMedicalGuide} =
	translations['pt-br'].commons;

export function useBookingNotFound({
	variant,
	flowType,
	specialtyNotAvailableOnline,
	allowsRequestScheduleRegistration,
}: UseBookingNotFoundProps) {
	const {isMobile} = useMediaQueries();
	const {callChatRoom, IS_LOADING: callChatRoomIsLoading} = useCallChatRoom();
	const setOpenDrawerState = useOpenDrawerFilterStore(state => state.setOpenDrawerState);
	const {SEARCH_PARAMS} = useQueryParams();
	const {dataHostNameState} = useHostName();
	const [openPhoneDrawerState, setOpenPhoneDrawerState] = useState<boolean>(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const {localityDistance, query} = SEARCH_PARAMS;
	const {handleChangeModals} = useBookingSummary();
	const bookingData = useBookingStore(state => state.bookingData);
	const specialtyHasTelehealth = bookingData.hasTelehealth;

	const isExam = flowType === FlowEnum.EXAM;
	const isHealth = flowType === FlowEnum.HEALTH;
	const isOdonto = flowType === FlowEnum.ODONTO;

	const isDistrictVariant = variant === BookingNotFoundStateVariantEnum.DISTRICT;
	const isUnitVariant = variant === BookingNotFoundStateVariantEnum.UNIT;
	const isBeBornWellVariant = variant === BookingNotFoundStateVariantEnum.BE_BORN_WELL;
	const maxDistanceFilter = isOdonto
		? MAX_DISTANCE_FILTER_UNITS_ODONTO
		: MAX_DISTANCE_FILTER_UNITS;
	const distanceFilterAtMaxDistance = Number(localityDistance) >= maxDistanceFilter;
	const distanceFilterBelowMaxDistance = Number(localityDistance) < maxDistanceFilter;
	const customBoxPadding = isMobile ? 2 : 4;

	const showLocalityButton =
		(isHealth || isExam) &&
		isUnitVariant &&
		distanceFilterBelowMaxDistance &&
		!specialtyNotAvailableOnline;
	const isAtMaxDistanceFilter = isUnitVariant && distanceFilterAtMaxDistance;

	const showChangeLocationButton = isAtMaxDistanceFilter || (isOdonto && !isDistrictVariant);
	const handleChangeLocation = () => handleChangeModals(ChangeFlowVariantEnum.LOCATION);

	const phoneNumbers = UNITS_CHANNELS_CONTACTS[dataHostNameState.product].phones;
	const phoneNumbersAlert = UNITS_CHANNELS_CONTACTS[dataHostNameState.product].alert;

	const isPhoneNumberMenuOpen = Boolean(anchorEl);

	const shouldShowRequestSchedulePermission =
		!!allowsRequestScheduleRegistration &&
		isHealth &&
		!query &&
		((isAtMaxDistanceFilter && isUnitVariant) || isDistrictVariant);

	const getUnitDescription = () => {
		const descriptions = {
			default: unitsFlow.notFoundModal.description,
			defaultExam: unitsFlow.notFoundModal.descriptionExam,
			defaultOdonto: unitsFlow.notFoundModal.descriptionOdonto,
			specialtyNotAvailableOnline:
				unitsFlow.notFoundModal.specialtyNotAvailableOnlineDescription,
			maxDistanceHealth: unitsFlow.notFoundModal.contactSupportHealth,
			maxDistanceExam: unitsFlow.notFoundModal.contactSupportExam,
			requestSchedulePermission: unitsFlow.notFoundModal.requestSchedulePermission,
		};

		if (specialtyNotAvailableOnline) return descriptions.specialtyNotAvailableOnline;
		if (shouldShowRequestSchedulePermission) return descriptions.requestSchedulePermission;
		if (isAtMaxDistanceFilter) {
			return isExam ? descriptions.maxDistanceExam : descriptions.maxDistanceHealth;
		}
		if (isExam) return descriptions.defaultExam;
		if (isOdonto) return descriptions.defaultOdonto;

		return descriptions.default;
	};

	const getDistrictDescription = () => {
		if (specialtyNotAvailableOnline) {
			return unitsFlow.notFoundModal.specialtyNotAvailableOnlineDescription;
		}
		if (shouldShowRequestSchedulePermission) {
			return unitsFlow.notFoundModal.requestSchedulePermission;
		}

		if (isHealth && specialtyHasTelehealth) {
			return districtFlow.notFoundModal.descriptionAllHealth;
		}

		if (isOdonto) {
			return districtFlow.notFoundModal.descriptionOdonto;
		}

		return isHealth
			? districtFlow.notFoundModal.descriptionHealth
			: districtFlow.notFoundModal.descriptionExam;
	};

	const {isEnabled: isSchedulingByMedicalGuideEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SCHEDULING_BY_MEDICAL_GUIDE,
	);

	const consultGuideText = isOdonto ? consultNetworkProviderOdonto : consultNetworkProviderHealth;
	const localityButtonText =
		isExam || isHealth ? unitsFlow.searchNearbyUnitsHealthExam : unitsFlow.searchNearbyUnits;

	const showLocalityOrChangeLocationButton =
		(showLocalityButton || showChangeLocationButton) && !shouldShowRequestSchedulePermission;

	const NOT_FOUND_CONTENT = {
		[BookingNotFoundStateVariantEnum.CITY]: {
			title: noResults,
			description: emptyDescription,
			guideButtonText: consultGuideText,
			chatButtonText: callToChat,
		},
		[BookingNotFoundStateVariantEnum.UF]: {
			title: noResults,
			description: emptyDescription,
			guideButtonText: consultGuideText,
			chatButtonText: callToChat,
		},
		[BookingNotFoundStateVariantEnum.SPECIALTY]: {
			title: noResults,
			description: emptyDescription,
			guideButtonText: consultGuideText,
			chatButtonText: callToChat,
		},
		[BookingNotFoundStateVariantEnum.DISTRICT]: {
			title: specialtyNotAvailableOnline
				? unitsFlow.notFoundModal.specialtyNotAvailableOnlineTitle
				: noResults,
			description: getDistrictDescription(),
			guideButtonText: consultNetworkProviderOdonto,
			chatButtonText: callToChat,
		},
		[BookingNotFoundStateVariantEnum.UNIT]: {
			title: specialtyNotAvailableOnline
				? unitsFlow.notFoundModal.specialtyNotAvailableOnlineTitle
				: noResults,
			description: getUnitDescription(),
			guideButtonText: consultGuideText,
			chatButtonText: callToChat,
		},
		[BookingNotFoundStateVariantEnum.PROFESSIONAL]: {
			title:
				isSchedulingByMedicalGuideEnabled && bookingData?.isBookingByMedicalGuideFlow
					? noResults
					: professionalFlow.notFoundModal.emptyTitle,
			description:
				isSchedulingByMedicalGuideEnabled && bookingData?.isBookingByMedicalGuideFlow
					? emptyDescriptionMedicalGuide
					: emptyDescription,
			guideButtonText: consultGuideText,
			chatButtonText: callToChat,
		},
		[BookingNotFoundStateVariantEnum.BE_BORN_WELL]: {
			title: unitsFlow.notFoundModal.specialtyNotAvailableOnlineTitle,
			description: unitsFlow.notFoundModal.gynecologyNotAvailableOnlineDescription,
			chatButtonText: callToChat,
		},
	};

	const handleCallChatRoom = async () => {
		let type = ChatRoomsEnum.APP;

		if (isBeBornWellVariant) {
			type = ChatRoomsEnum.BE_BORN_WELL;
		}

		if (isOdonto) {
			type = ChatRoomsEnum.ODONTO;
		}

		if (isExam) {
			type = ChatRoomsEnum.EXAMS;
		}
		await callChatRoom(type);
	};

	const handleOpenDrawer = () => {
		setOpenDrawerState(true);
	};

	const handleOpenPhoneMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (isMobile) {
			setOpenPhoneDrawerState(true);
			return;
		}
		setAnchorEl(event.currentTarget);
	};

	const handleTogglePhoneDrawer = () => {
		setOpenPhoneDrawerState(prev => !prev);
	};

	const handleClosePhoneMenu = () => {
		setAnchorEl(null);
	};

	const showUnitChangeButton =
		bookingData?.isBookingByMedicalGuideFlow && isSchedulingByMedicalGuideEnabled;

	const showCallButton =
		isUnitVariant ||
		isBeBornWellVariant ||
		(isDistrictVariant && specialtyNotAvailableOnline) ||
		(isDistrictVariant && allowsRequestScheduleRegistration);
	const isChatButtonOutlined =
		showLocalityOrChangeLocationButton ||
		shouldShowRequestSchedulePermission ||
		showUnitChangeButton ||
		(isDistrictVariant && !specialtyNotAvailableOnline);
	const showDistrictChangeLocation =
		isDistrictVariant && !bookingData.hasTelehealth && !specialtyNotAvailableOnline;
	const showDistrictGoToUnit =
		isDistrictVariant && bookingData.hasTelehealth && !specialtyNotAvailableOnline;

	const showImageV2 = isUnitVariant || isBeBornWellVariant || specialtyNotAvailableOnline;

	return {
		handleClosePhoneMenu,
		handleTogglePhoneDrawer,
		handleOpenPhoneMenu,
		handleOpenDrawer,
		handleCallChatRoom,
		NOT_FOUND_CONTENT,
		localityButtonText,
		isPhoneNumberMenuOpen,
		showLocalityButton,
		openPhoneDrawerState,
		isUnitVariant,
		isBeBornWellVariant,
		isDistrictVariant,
		phoneNumbers,
		phoneNumbersAlert,
		anchorEl,
		customBoxPadding,
		isHealth,
		shouldShowRequestSchedulePermission,
		handleChangeLocation,
		showChangeLocationButton,
		showLocalityOrChangeLocationButton,
		showCallButton,
		isChatButtonOutlined,
		showDistrictChangeLocation,
		showDistrictGoToUnit,
		showImageV2,
		callChatRoomIsLoading,
		showUnitChangeButton,
	};
}
